import type { ProductSkuMap } from '../hooks/useProductSkuMap';
import * as types from '../types';

/**
 * Partner offers whose fixed coupon covers the hardware and a 12-month plan, leaving the shopper to pay only the free
 * trial shipping fee (The Pets Table's parent coupon tpt1y hands out unique tpt1y-xxxx codes). Web sends the parent
 * coupons in the site config from its ecommerce:shippingOnlyPartnerParentCoupons flag; this is the fallback until the
 * config says otherwise.
 */
export const DEFAULT_SHIPPING_ONLY_PARTNER_PARENT_COUPONS = ['tpt1y'];

export function isShippingOnlyPartnerCoupon(couponCode: string | null | undefined, parentCoupons: string[]) {
  const code = couponCode?.toLowerCase();
  return !!code && parentCoupons.some((parentCoupon) => code.startsWith(`${parentCoupon.toLowerCase()}-`));
}

/**
 * The offer's kit price. The shop's catalog lists the bundled Mini at $0, but web charges the offer's kit at the Mini's
 * list price (HARDWARE_FIRST_PRICING: $99, C$149) so the partner coupon's discount on it shows on the invoice. Partner
 * offers only check out where the free trial shipping fee is priced, USD and CAD.
 */
export function shippingOnlyPartnerHardwareValueInCents(currency: string | undefined) {
  return currency === types.FiCurrency.CAD ? 14900 : 9900;
}

/**
 * The partner offer covers one Mini: the first non-gift Mini bundle in the cart on a 12-month plan, the plans its coupon
 * covers. Mirrors shippingOnlyPartnerMiniCartItemId in web's ConsumerStoreCart, which prices only that kit at $99 so
 * the offer's coupon covers it.
 */
export function shippingOnlyPartnerMiniCartItemId(
  cart: Pick<types.Cart, 'cartItems' | 'couponCode'>,
  productSkuMap: ProductSkuMap,
  parentCoupons: string[],
) {
  if (!isShippingOnlyPartnerCoupon(cart.couponCode, parentCoupons)) {
    return undefined;
  }
  return Object.values(cart.cartItems).find((item) => isShippingOnlyPartnerMiniBundle(item, productSkuMap))?.cartItemId;
}

/**
 * Whether the cart has a Mini the partner offer covers, which is what charges it the free trial shipping fee. The
 * partner's code alone doesn't qualify a cart; web rejects free trial shipping without one.
 */
export function cartHasShippingOnlyPartnerOffer(
  cart: Pick<types.Cart, 'cartItems' | 'couponCode'>,
  productSkuMap: ProductSkuMap,
  parentCoupons: string[],
) {
  return shippingOnlyPartnerMiniCartItemId(cart, productSkuMap, parentCoupons) !== undefined;
}

function isShippingOnlyPartnerMiniBundle(item: types.CartItem, productSkuMap: ProductSkuMap) {
  // a bundle is a kit sold with its membership (lib/cart's isBundleCartItem; not imported to keep this module light)
  if (
    !('subscriptionLineItem' in item) ||
    item.isGift ||
    productSkuMap.get(item.lineItem.sku)?.product.id !== types.miniCollarId
  ) {
    return false;
  }
  const plan = productSkuMap.get(item.subscriptionLineItem.sku)?.product;
  return !!plan && types.isModuleSubscriptionProduct(plan) && plan.renewalMonths === 12;
}
